import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { API_CONFIG } from "../config/api.config";
import jwtTokenService from "../services/jwtTokenService";
import HotelDetailView from "./HotelDetail/HotelDetailView";

const HotelDetailPage = () => {
  const { hotelId } = useParams();
  const navigate = useNavigate();
  const [hotel, setHotel] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [hotelPhotos, setHotelPhotos] = useState({});
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    checkAuth();
    fetchHotelDetails();
    // Scroll to top on mount
    window.scrollTo(0, 0);
  }, [hotelId]);

  const checkAuth = () => {
    setIsAuthenticated(jwtTokenService.isAuthenticated());
  };

  const fetchHotelDetails = async () => {
    try {
      setLoading(true);

      // Fetch hotel details
      const hotelResponse = await axios.get(
        `${API_CONFIG.BASE_URL}/hotels/${hotelId}`
      );
      // Map back-end fields to front-end expected fields if necessary
      const hotelData = {
        ...hotelResponse.data,
        hotelId: hotelResponse.data.hotelId || hotelResponse.data.hotel_id,
        hotelName: hotelResponse.data.hotelName || hotelResponse.data.hotel_name,
      };
      setHotel(hotelData);

      // Fetch available rooms for this hotel
      const roomsResponse = await axios.get(
        `${API_CONFIG.BASE_URL}/hotels/${hotelId}/rooms`
      );
      setRooms(roomsResponse.data);

      // Fetch hotel photos
      const photosResponse = await axios.get(
        `${API_CONFIG.BASE_URL}/hotel-photos/${hotelId}`
      );
      const mappedPhotos = (photosResponse.data || [])
        .filter(p => p && (p.photoUrl || p.photo_url || p.url))
        .map(p => ({
          ...p,
          photoUrl: p.photoUrl || p.photo_url || p.url
        }));
      
      setHotelPhotos({ [hotelId]: mappedPhotos });

    } catch (error) {
      console.error("Error fetching hotel details:", error);
      toast.error("Failed to load hotel details");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToHotels = () => {
    navigate("/HotelRoomsPage");
  };

  const handleRoomBooking = (room) => {
    if (!isAuthenticated) {
      toast.info("Please login to book a room");
      navigate("/login");
      return;
    }
    navigate(`/room-booking/${room.roomId || room.room_id}`, {
      state: { hotel, room }
    });
  };

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", backgroundColor: "#f8f9fa", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
           <div style={{ width: "48px", height: "48px", border: "4px solid #f3f3f3", borderTop: "4px solid #EE2E24", borderRadius: "50%", animation: "spin 1s linear infinite", margin: "0 auto 16px auto" }}></div>
           <p style={{ color: "#666", fontWeight: "600" }}>Loading Hotel Details...</p>
           <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (!hotel) {
    return (
      <div style={{ minHeight: "100vh", backgroundColor: "#f8f9fa", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <h2 style={{ fontSize: "24px", fontWeight: "800", color: "#1a1a1a", marginBottom: "16px" }}>Hotel Not Found</h2>
          <button 
            onClick={() => navigate("/HotelRoomsPage")}
            style={{ padding: "10px 24px", background: "#EE2E24", color: "#fff", border: "none", borderRadius: "8px", fontWeight: "700", cursor: "pointer" }}
          >
            Back to Hotels
          </button>
        </div>
      </div>
    );
  }

  return (
    <HotelDetailView
      selectedHotel={hotel}
      rooms={rooms}
      hotelPhotos={hotelPhotos}
      isAuthenticated={isAuthenticated}
      navigate={navigate}
      handleBackToHotels={handleBackToHotels}
      handleRoomBooking={handleRoomBooking}
    />
  );
};

export default HotelDetailPage;
